import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  CarouselImage,
  DateOverlay,
  ImagePlaceholder,
} from './ImageCarousel.styled';
import type { ImageWithDate } from '../../../../types/contactLog';
import { AllPhotosModal } from '../AllPhotosModal/AllPhotosModal';
import { PhotoDetailModal } from '../AllPhotosModal/PhotoDetailModal';

interface ImageCarouselProps {
  images: ImageWithDate[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
}

const ImageCarouselComponent: React.FC<ImageCarouselProps> = ({
  images,
  currentIndex,
  onIndexChange,
}) => {
  // 画像タップで開く「全ての写真」モーダルの開閉状態（写真が複数のとき）
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 写真が1枚のときに開く詳細オーバーレイの表示対象（null で非表示）
  const [detailImage, setDetailImage] = useState<ImageWithDate | null>(null);

  // 画像タップ時のハンドラ: 複数なら一覧、1枚なら詳細を開く
  const handleImageTap = () => {
    if (images.length === 1) {
      setDetailImage(images[0]);
    } else {
      setIsModalOpen(true);
    }
  };

  // 防御的チェック: インデックスが範囲外の場合は0にフォールバック
  const safeIndex = currentIndex < images.length ? currentIndex : 0;

  // クロスフェード用: 退場中の画像URL（null = トランジションなし）
  // URLで保持することで、imagesが変化しても退場アニメーションが正しく表示される
  const [outgoingImageUrl, setOutgoingImageUrl] = useState<string | null>(null);
  const crossfadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useEffectではなくrender中に同期検出することで、新画像が一瞬ちらつく問題を防ぐ
  // （useEffectはレンダー後に実行されるため、1フレーム遅延が生じる）
  const [prevSafeIndex, setPrevSafeIndex] = useState(safeIndex);
  if (prevSafeIndex !== safeIndex) {
    setPrevSafeIndex(safeIndex);
    setOutgoingImageUrl(images[prevSafeIndex]?.url ?? null);
  }

  // outgoingImageUrl が設定されたら 500ms 後にクリア
  useEffect(() => {
    if (!outgoingImageUrl) return;
    if (crossfadeTimerRef.current) clearTimeout(crossfadeTimerRef.current);
    crossfadeTimerRef.current = setTimeout(() => {
      setOutgoingImageUrl(null);
      crossfadeTimerRef.current = null;
    }, 500);
  }, [outgoingImageUrl]);

  // アンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (crossfadeTimerRef.current) clearTimeout(crossfadeTimerRef.current);
    };
  }, []);

  // 5秒ごとに自動でスライドショー
  useEffect(() => {
    if (images.length <= 1) return; // 1枚以下なら自動切り替え不要

    const intervalId = setInterval(() => {
      onIndexChange(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
    }, 5000);

    return () => clearInterval(intervalId); // クリーンアップ
  }, [currentIndex, images.length, onIndexChange]);

  // 現在の画像の日付を取得（YYYY-MM-DD → YYYY/MM/DD形式に変換）
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${year}/${parseInt(month, 10)}/${parseInt(day, 10)}`;
  };

  // インデックスが範囲外になった場合、親コンポーネントに通知
  useEffect(() => {
    if (currentIndex >= images.length && images.length > 0) {
      onIndexChange(0);
    }
  }, [currentIndex, images.length, onIndexChange]);

  const currentImage = images[safeIndex];
  const dateText = currentImage?.date ? formatDate(currentImage.date) : '';

  return (
    <>
      <Container>
        {dateText && <DateOverlay>{dateText}</DateOverlay>}
        {images.length > 0 ? (
          <>
            {/* 下層: 新しい画像（トランジション中はfadeIn、それ以外はstable） */}
            <CarouselImage
              $imageUrl={currentImage.url}
              $animState={outgoingImageUrl !== null ? 'entering' : 'stable'}
              onClick={handleImageTap}
              role="button"
              tabIndex={0}
              aria-label="写真を拡大表示"
              onKeyDown={(e) => {
                // キーボード操作でも開けるよう Enter/Space に対応
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleImageTap();
                }
              }}
            />
            {/* 上層: 退場中の古い画像（fadeOut） */}
            {outgoingImageUrl !== null && (
              <CarouselImage
                $imageUrl={outgoingImageUrl}
                $animState="leaving"
              />
            )}
          </>
        ) : (
          <ImagePlaceholder>📷</ImagePlaceholder>
        )}
      </Container>
      <AllPhotosModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        images={images}
      />
      <PhotoDetailModal image={detailImage} onClose={() => setDetailImage(null)} />
    </>
  );
};

// React.memoで最適化: propsが変わらない限り再レンダリングしない
export const ImageCarousel = React.memo(ImageCarouselComponent);

export default ImageCarousel;
